import React, { useState, useEffect } from 'react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { useSelector } from 'react-redux';
import { useRouteMatch, Route, Switch, Link } from 'react-router-dom';
import { formatValue } from '../common/utils';

import { dateService } from '../../../services/date';
import { session } from '../../../services/session';
import Constants from '../../../app.constants';
import { paymentsService } from './payments-service';

import Overview from './overview';
import PaymentSetup from './payment-setup';
import { TabbedCards, Tab } from '../../ui/tabs';

import './payments.scss';

const earningsDescription = `Earnings are the total amount of money you’ve made on Narrativ.
	Once you start earning on Narrativ, you can track the status of those Earnings
	to see how much you’re expected to be paid.`;

const payoutsDescription = `Payouts are Earnings that have already been deposited to your account.
	You can expect to receive payouts approximately 90 days after each Earnings Month ends.`;

const Payments = ({ flags, match }) => {
  const endpoint = `/api/v0/publishers/${session.orgId}/fake/stats/`;
  const accountLink = useSelector(state => state.accountLink) || {};
  const { url, path } = useRouteMatch();

  const stripeStatus = paymentsService.getStatus(accountLink);
  const payoutsDateSelectorOptions = paymentsService.populateDateSelector();
  const earningsDateSelectorOptions = paymentsService.populateDateSelector();

  const [payoutBreakdownDate, setPayoutBreakdownDate] = useState(null);
  const [showMocks, setShowMocks] = useState(false);

  const [payoutsData, setPayoutsData] = useState([]);
  const [payoutsBreakdownData, setPayoutsBreakdownData] = useState([]);
  const [earningsData, setEarningsData] = useState([]);
  const [payoutsCardData, setPayoutsCardData] = useState([]);
  const [earningsCardData, setEarningsCardData] = useState([]);

  const [earningsTotal, setEarningsTotal] = useState(0);
  const [payoutTotal, setPayoutTotal] = useState(0);
  const [payoutsBreakdownTotal, setPayoutsBreakdownTotal] = useState(0);

  const [earningsSort, setEarningsSort] = useState(paymentsService.earningsColumns[1].dataKey);
  const [payoutsSort, setPayoutsSort] = useState(paymentsService.payoutsColumns[0].dataKey);
  const [payoutsBreakdownSort, setPayoutsBreakdownSort] = useState(
    paymentsService.payoutsBreakdownColumns[0].dataKey,
  );
  const [earningsSortDirection, setEarningsSortDirection] = useState('desc');
  const [payoutsSortDirection, setPayoutsSortDirection] = useState('desc');
  const [payoutsBreakdownSortDirection, setPayoutsBreakdownSortDirection] = useState('desc');

  const [earningsPeriod, setEarningsPeriod] = useState(earningsDateSelectorOptions[0]);
  const [payoutsPeriod, setPayoutsPeriod] = useState(payoutsDateSelectorOptions[0]);
  const [payoutsBreakdownPeriod, setPayoutsBreakdownPeriod] = useState(
    earningsDateSelectorOptions[0],
  );

  useEffect(() => {
    if (!showMocks) {
      getEarningsData();
    }
  }, [earningsPeriod, earningsSort, earningsSortDirection]);

  useEffect(() => {
    if (!showMocks) {
      getPayoutsData();
    }
  }, [payoutsPeriod, payoutsSort, payoutsSortDirection]);

  useEffect(() => {
    if (!showMocks && payoutBreakdownDate) {
      getPayoutsBreakdownData();
    }
  }, [
    payoutsBreakdownPeriod,
    payoutsBreakdownSort,
    payoutsBreakdownSortDirection,
    payoutBreakdownDate,
  ]);

  useEffect(() => {
    if (showMocks) {
      displayMockData();
    }
  }, [showMocks]);

  async function getEarningsData() {
    const params = {
      ...earningsPeriod.params,
      order_by: earningsSort,
      direction: earningsSortDirection,
    };
    const earningsRes = await paymentsService.getEarnings(match.params.publisherId, params);
    const data = earningsRes.data.data[0];
    if (data.data) {
      const earningsDataFormatted = paymentsService.formatEarningsData(data);

      setEarningsData(earningsDataFormatted);

      const earningCardsFormatted = paymentsService.formatEarningsCardData(data);
      setEarningsCardData(earningCardsFormatted);

      setEarningsTotal(data.totals.total_earnings);
    }
  }

  async function getPayoutsData() {
    const params = {
      ...payoutsPeriod.params,
      order_by: payoutsSort,
      direction: payoutsSortDirection,
    };
    const res = await paymentsService.getPayouts(match.params.publisherId, params);
    const data = res.data.data[0];
    const { payouts, cardData } = paymentsService.formatPayoutsData(data);

    setPayoutsData(payouts);
    setPayoutsCardData(cardData);
    setPayoutTotal(data.totals.total_transfer_sum);
  }

  async function getPayoutsBreakdownData() {
    const params = {
      payout_date: payoutBreakdownDate,
      order_by: payoutsBreakdownSort,
      direction: payoutsBreakdownSortDirection,
    };
    const res = await paymentsService.getPayoutsBreakdown(match.params.publisherId, params);
    const data = res.data.data[0];
    setPayoutsBreakdownData(paymentsService.formatPayoutsBreakdownData(data.payments));
    setPayoutsBreakdownTotal(data.total);
  }

  function displayMockData() {
    const { payoutsRes, earningsRes, payoutsBreakdownRes } = paymentsService.getMockData();

    const earningsDataFormatted = paymentsService.formatEarningsData(earningsRes);
    setEarningsData(earningsDataFormatted);
    const earningCardsFormatted = paymentsService.formatEarningsCardData(earningsRes);
    setEarningsCardData(earningCardsFormatted);
    setEarningsTotal(earningsRes.totals.total_earnings);

    const { payouts, cardData } = paymentsService.formatPayoutsData(payoutsRes);
    setPayoutsData(payouts);
    setPayoutsCardData(cardData);
    setPayoutTotal(payoutsRes.totals.total_transfer_sum);

    setPayoutsBreakdownData(
      paymentsService.formatPayoutsBreakdownData(payoutsBreakdownRes.payments),
    );
    setPayoutsBreakdownTotal(payoutsBreakdownRes.total);
  }

  function getDisabledReasonError() {
    return (
      <div className="disabled-reason-error">
        <span>
          Your account has been disabled because an error occurred with your Stripe payment setup.
        </span>
        <span>{' To reactivate your account, please '}</span>
        <a
          className="visit-stripe-dashboard"
          href={accountLink.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          visit your Stripe dashboard.
        </a>
      </div>
    );
  }

  const disabledReasonError = stripeStatus === 'disabled' ? getDisabledReasonError() : null;

  const mocksButton = ['local', 'staging'].includes(Constants.ENV) ? (
    <button className="mocks-btn" onClick={() => setShowMocks(!showMocks)}>
      Mocks
    </button>
  ) : null;

  return (
    <div className={`billing-payments stripe-status-${stripeStatus} fs-unmask`}>
      {mocksButton}
      <TabbedCards styleType="block" fittedUnderline={true}>
        <Tab name="Payouts" path="payouts" isShowing={flags.billingTabs}>
          <div className="section payouts">
            <Switch>
              <Route exact path={`${path}/payouts`}>
                {disabledReasonError}
                <Overview
                  data={payoutsData}
                  title="Payouts"
                  description={payoutsDescription}
                  cards={payoutsCardData}
                  columns={paymentsService.payoutsColumns}
                  totalLabel="Paid"
                  totalValue={formatValue(payoutTotal, 'currency')}
                  sortField={payoutsSort}
                  sortDirection={payoutsSortDirection}
                  setSortField={setPayoutsSort}
                  setSortDirection={setPayoutsSortDirection}
                  setMonth={setPayoutsPeriod}
                  month={payoutsPeriod}
                  monthOptions={payoutsDateSelectorOptions}
                  params={payoutsPeriod.params}
                  endpoint={endpoint}
                  rowPath={rowData => `${url}/payouts/${rowData.payout_date}`}
                  setPayoutBreakdownDate={setPayoutBreakdownDate}
                  rowIdField="key"
                />
              </Route>
              <Route exact path={`${path}/payouts/:payoutPeriod?`}>
                <Link className="back" to={`${url}/payouts`}>
                  <span className="arrow-left"></span>
                  <span>Back to Payouts</span>
                </Link>
                <Overview
                  data={payoutsBreakdownData}
                  title=""
                  description=""
                  columns={paymentsService.payoutsBreakdownColumns}
                  totalLabel="Paid"
                  totalValue={formatValue(payoutsBreakdownTotal, 'currency')}
                  sortField={payoutsBreakdownSort}
                  sortDirection={payoutsBreakdownSortDirection}
                  setSortField={setPayoutsBreakdownSort}
                  setSortDirection={setPayoutsBreakdownSortDirection}
                  params={payoutsPeriod.params}
                  endpoint={endpoint}
                  rowIdField="key"
                  controlsLabel={`Payouts for ${dateService.formatDisplayDate(
                    payoutBreakdownDate,
                  )}`}
                />
              </Route>
            </Switch>
          </div>
        </Tab>
        <Tab name="Earnings" path="earnings" isShowing={flags.billingTabs}>
          <div className="section earnings">
            <Overview
              data={earningsData}
              title="Earnings"
              description={earningsDescription}
              cards={earningsCardData}
              columns={paymentsService.earningsColumns}
              totalLabel="Amount Earned"
              totalValue={formatValue(earningsTotal, 'currency')}
              sortField={earningsSort}
              sortDirection={earningsSortDirection}
              setSortField={setEarningsSort}
              setSortDirection={setEarningsSortDirection}
              setMonth={setEarningsPeriod}
              month={earningsPeriod}
              monthOptions={earningsDateSelectorOptions}
              params={earningsPeriod.params}
              endpoint={endpoint}
              rowIdField="key"
            />
          </div>
        </Tab>
        <Tab
          name="Payment Setup"
          path="payment-setup"
          isShowing={true}
          {...(paymentsService.showAlertIcon(accountLink) ? { icon: 'filledAlert' } : {})}
          iconSide="right"
        >
          <PaymentSetup accountLink={accountLink} stripeStatus={stripeStatus} match={match} />
        </Tab>
      </TabbedCards>
    </div>
  );
};

export default withLDConsumer()(Payments);
